import { groupSpacingAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/groupSpacing'
import { moduleSpacingAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/moduleSpacing'
import { rackingTypeAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/rackingType'
import { rollAccessorConfig } from 'projectSections/designPanels/PanelModuleGrid/accessors/roll'
import { StudioSystemType } from 'types/global'
import { syncGroundClearanceToPanelSettings } from './Inputs'
import {
  SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES,
  SOLARNEST_REQUIRED_TILT_DEGREES,
} from './mounting_systems/solarNestWindSnowMax'
import { getSolarNestRidgeValleySpacingMeters } from './mounting_systems/solarNestRidgeValleySpacing'
import { fastRackDropdownHandlers } from './mounting_systems/FastRack/FastRackDropdownHandlers'
import { novaDropdownHandlers } from './mounting_systems/Nova/NovaDropdownHandlers'
import { snapnrackDropdownHandlers } from './mounting_systems/SnapNRack/SnapnrackDropdownHandlers'
import { solarPortDropdownHandlers } from './mounting_systems/SolarPort/SolarPortDropdownHandlers'
import { setRelativeTiltForModuleGrids } from './utils'

export type DropdownChangeHandler = (
  system: StudioSystemType,
  translate: (key: string, options?: Record<string, unknown>) => string
) => void

const handleSolarNestEastWestTilt: DropdownChangeHandler = (system, translate) => {
  system.moduleGrids().forEach((moduleGrid) => {
    rackingTypeAccessorConfig.set({ moduleGrid, value: 'DUAL_TILT_RACK' })
  })
  setRelativeTiltForModuleGrids(system, SOLARNEST_REQUIRED_TILT_DEGREES)
  system.moduleGrids().forEach((moduleGrid) => {
    rollAccessorConfig.set({ moduleGrid, value: 0 })
  })
  const firstGrid = system.moduleGrids()[0]
  const moduleType = firstGrid?.moduleType?.()
  const frameWidthMm = (moduleType as { thickness?: number } | undefined)?.thickness ?? 35
  const { ridgeM, valleyM } = getSolarNestRidgeValleySpacingMeters(frameWidthMm)
  system.moduleGrids().forEach((moduleGrid) => {
    const currentModule = moduleSpacingAccessorConfig.get(moduleGrid)
    const currentGroup = groupSpacingAccessorConfig.get(moduleGrid)
    moduleSpacingAccessorConfig.set({ moduleGrid, value: [currentModule[0], ridgeM] })
    groupSpacingAccessorConfig.set({ moduleGrid, value: [currentGroup[0], valleyM] })
  })
  if (system.mounting === 'solarNestGroundMount' && system.integration_json?.solarNestGroundMount) {
    system.integration_json.solarNestGroundMount.groundClearance = SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES
    syncGroundClearanceToPanelSettings(system, SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES)
  }
  window.Designer?.showNotification(
    translate('Dual tilt set to %{degrees} degrees and module spacing adjusted to be compatible with selected mounting system.', {
      degrees: SOLARNEST_REQUIRED_TILT_DEGREES,
    }),
    'info'
  )
}

const handleSolarNestTripleTilt: DropdownChangeHandler = (system, translate) => {
  system.moduleGrids().forEach((moduleGrid) => {
    if (moduleGrid.moduleLayout() !== 'landscape') {
      window.editor.execute(
        new window.RedrawModulesCommand(moduleGrid, 'moduleLayout', 'landscape', moduleGrid.moduleLayout())
      )
    }
    rackingTypeAccessorConfig.set({ moduleGrid, value: 'DUAL_TILT_RACK' })
  })

  // Set relative tilt (E/W tilt / dual tilt) to 8 degrees
  setRelativeTiltForModuleGrids(system, SOLARNEST_REQUIRED_TILT_DEGREES)

  // Set triple tilt (N/S tilt / roll) to 8 degrees
  system.moduleGrids().forEach((moduleGrid) => {
    rollAccessorConfig.set({ moduleGrid, value: SOLARNEST_REQUIRED_TILT_DEGREES })
  })

  if (system.mounting === 'solarNestGroundMount' && system.integration_json?.solarNestGroundMount) {
    system.integration_json.solarNestGroundMount.groundClearance = SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES
    syncGroundClearanceToPanelSettings(system, SOLARNEST_DEFAULT_GROUND_CLEARANCE_INCHES)
  }
  window.Designer?.showNotification(
    translate(
      'Triple tilt set to %{degrees} degrees and module spacing adjusted to be compatible with selected mounting system.',
      { degrees: SOLARNEST_REQUIRED_TILT_DEGREES }
    ),
    'info'
  )
}

const handleVanDerValkTopographyNotExposed: DropdownChangeHandler = (system) => {
  if (system.mounting !== 'vanDerValk') return
  const base = (system.integration_json || {}) as Record<string, unknown>
  const prevVdv = base.vanDerValk
  const prevObj =
    typeof prevVdv === 'object' && prevVdv !== null ? { ...(prevVdv as Record<string, unknown>) } : {}
  delete prevObj.topography_zone
  delete prevObj.topography_slope
  system.integration_json = { ...base, vanDerValk: prevObj } as typeof system.integration_json
  window.editor.signals.objectChanged.dispatch(system, 'mounting')
}

const registry: Record<string, Record<string, Record<string, DropdownChangeHandler>>> = {
  vanDerValk: {
    topography_type: {
      'Not exposed': handleVanDerValkTopographyNotExposed,
    },
  },
  solarNestRooftop: {
    tiltOption: {
      eastWestTilt: handleSolarNestEastWestTilt,
      tripleTilt: handleSolarNestTripleTilt,
    },
  },
  solarNestGroundMount: {
    tiltOption: {
      eastWestTilt: handleSolarNestEastWestTilt,
      tripleTilt: handleSolarNestTripleTilt,
    },
  },
  solarPortGroundMount: solarPortDropdownHandlers,
  nova: novaDropdownHandlers,
  fastRack: fastRackDropdownHandlers,
  snapnrackUltraRail: snapnrackDropdownHandlers,
}

export function getDropdownChangeHandler(
  mountingName: string,
  variableName: string,
  value: string
): DropdownChangeHandler | undefined {
  return registry[mountingName]?.[variableName]?.[value]
}
